import { useState, useEffect, useCallback } from 'react';
import {
  api,
  tokenStore,
  loginRequest,
  registerRequest,
  meRequest,
  logoutLocal,
} from '../services/api';

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Bootstrap: if we already have a token, restore the session by calling /me.
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      if (!tokenStore.getAccess() && !tokenStore.getApiKey()) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const me = await meRequest();
        if (cancelled) return;
        setUser({
          user_id: me.user_id,
          vi_user_id: me.vi_user_id,
          email: me.email,
          display_name: me.display_name,
          photo_url: me.photo_url,
          role: me.role,
          tier: me.tier,
        });
        api.setViUserId(me.vi_user_id);
        api.createSession().catch(() => {});
        reportWebDevice().catch(() => {});
      } catch {
        if (!cancelled) {
          tokenStore.clear();
          api.setViUserId(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username, password) => {
    setError(null);
    setLoading(true);
    try {
      const data = await loginRequest(username, password);
      setUser(data.user);
      api.createSession().catch(() => {});
      reportWebDevice().catch(() => {});
      return true;
    } catch (err) {
      if (err.code === 'invalid_credentials') {
        setError('invalid_credentials');
      } else if (err.code === 'user_disabled') {
        setError('user_disabled');
      } else {
        setError(err.message || 'login_failed');
      }
      setUser(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const register = useCallback(async ({ email, password, displayName }) => {
    setError(null);
    setLoading(true);
    try {
      const data = await registerRequest({ email, password, displayName });
      setUser(data.user);
      api.createSession().catch(() => {});
      reportWebDevice().catch(() => {});
      return { ok: true, apiKey: data.api_key };
    } catch (err) {
      if (err.code === 'email_taken') {
        setError('email_taken');
      } else if (err.code === 'invalid_email') {
        setError('invalid_email');
      } else {
        setError(err.message || 'register_failed');
      }
      setUser(null);
      return { ok: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    logoutLocal();
    setUser(null);
  }, []);

  return {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
  };
}

async function reportWebDevice() {
  const deviceId = api.getDeviceId();
  await api.reportDevice({
    device_id: deviceId,
    device_token: null,
    store: 'web',
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    timezone: new Date().getTimezoneOffset() * -1,
    user_agent: navigator.userAgent,
  });
}
